import { Controller } from "@hotwired/stimulus"

// Team Tasks — the private task page (Miguel Serrano Torres, 2026-09-02).
// The list is server-rendered; every create/update/comment/delete talks
// JSON to the server and swaps the affected row or panel piece in place.
// Filters are a plain GET form. Nothing here reloads the page.
export default class extends Controller {
  static targets = [
    "modal", "panelBody", "detail",
    "newModal", "newForm", "newPeople", "newDue", "createBtn",
    "newDropzone", "newFileInput", "newFileChips",
    "files", "dropzone", "fileInput", "fileChips",
    "table", "list", "group", "empty", "openCount", "filters", "handle",
    "people", "comments", "commentInput", "commentCount", "deleteModal"
  ]

  static values = {
    tasksUrl: String,
    view: String,
    sort: String,
    openId: Number
  }

  initialize() {
    this._newFiles = []
    this._dragId = null
    this._justDragged = false
  }

  connect() {
    this._onKey = (e) => { if (e.key === "Escape") { this.closeNew(); this.close() } }
    document.addEventListener("keydown", this._onKey)
    this._armDrag()
    if (this.openIdValue) this.openById(this.openIdValue)
  }

  // ── Drag to reorder / move between projects ───────────────────────
  // Native HTML5 drag: the whole row is draggable while the list is in
  // "My order"; the handle just says so. A drop between rows reorders,
  // a drop into another project group moves the task there as well.

  get _sortable() { return this.sortValue === "manual" }

  _armDrag() {
    this.element.querySelectorAll("tr[data-task-id]").forEach((row) => { row.draggable = this._sortable })
    this.element.querySelectorAll("[data-team-tasks-target='handle']").forEach((h) => h.classList.toggle("opacity-30", !this._sortable))
  }

  handleTargetConnected(handle) {
    const row = handle.closest("tr[data-task-id]")
    if (row) row.draggable = this._sortable
    handle.classList.toggle("opacity-30", !this._sortable)
  }

  dragStart(event) {
    if (!this._sortable) { event.preventDefault(); this._toast(this._t("sort_to_drag", "Switch the sort to My order to drag tasks."), "error"); return }
    const row = event.currentTarget
    this._dragId = row.dataset.taskId
    this._dragFromGroup = row.parentElement?.dataset.group || null
    row.classList.add("opacity-50")
    event.dataTransfer.effectAllowed = "move"
    try { event.dataTransfer.setData("text/plain", this._dragId) } catch { /* Safari */ }
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("opacity-50")
    this._clearDropMarks()
    this._justDragged = true
    setTimeout(() => { this._justDragged = false }, 50)
  }

  dragOverRow(event) {
    if (!this._dragId) return
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "move"
    const row = event.currentTarget
    if (row.dataset.taskId === this._dragId) return
    const rect = row.getBoundingClientRect()
    const before = event.clientY < rect.top + rect.height / 2
    this._clearDropMarks()
    row.classList.add(before ? "drop-before" : "drop-after", before ? "border-t-2" : "border-b-2", "border-brand")
  }

  dragLeaveRow(event) {
    event.currentTarget.classList.remove("drop-before", "drop-after", "border-t-2", "border-b-2", "border-brand")
  }

  dropOnRow(event) {
    if (!this._dragId) return
    event.preventDefault()
    event.stopPropagation()
    const target = event.currentTarget
    const before = target.classList.contains("drop-before")
    this._clearDropMarks()
    const dragged = this._row(this._dragId)
    if (!dragged || dragged === target) return
    target.parentElement.insertBefore(dragged, before ? target : target.nextSibling)
    this._finishDrop(dragged, target.parentElement)
  }

  dragOverGroup(event) {
    if (!this._dragId) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    event.currentTarget.classList.add("bg-brand/5")
  }

  dragLeaveGroup(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return
    event.currentTarget.classList.remove("bg-brand/5")
  }

  // Dropped on the group itself (its header or empty space): goes last in that group.
  dropOnGroup(event) {
    if (!this._dragId) return
    event.preventDefault()
    const tbody = event.currentTarget
    tbody.classList.remove("bg-brand/5")
    this._clearDropMarks()
    const dragged = this._row(this._dragId)
    if (!dragged) return
    tbody.appendChild(dragged)
    this._finishDrop(dragged, tbody)
  }

  async _finishDrop(dragged, tbody) {
    const movedId = this._dragId
    this._dragId = null
    const fromGroup = this._dragFromGroup
    const toGroup = tbody.dataset.group || null
    const ids = Array.from(this.element.querySelectorAll("tr[data-task-id]")).map((r) => r.dataset.taskId)
    const body = { moved_id: movedId, ids }
    if (this.viewValue === "by_project" && toGroup && toGroup !== fromGroup) body.project_id = toGroup

    const data = await this._send("POST", `${this.tasksUrlValue}/reorder`, body)
    if (!data) { this.applyFilters(); return }
    this._replaceRow(data)
    if (body.project_id) {
      const source = fromGroup ? this.element.querySelector(`tbody[data-group="${fromGroup}"]`) : null
      this._afterRemove(source)
      this._refreshGroupCounts()
      this._toast(this._t("moved", "Task moved"))
    } else {
      this._toast(this._t("reordered", "Order saved"))
    }
    this._armDrag()
  }

  _refreshGroupCounts() {
    this.element.querySelectorAll("tbody[data-group]").forEach((g) => {
      const c = g.querySelector("[data-group-count]")
      if (c) c.textContent = g.querySelectorAll("tr[data-task-id]").length
    })
  }

  _clearDropMarks() {
    this.element.querySelectorAll(".drop-before, .drop-after").forEach((r) => r.classList.remove("drop-before", "drop-after", "border-t-2", "border-b-2", "border-brand"))
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
  }

  // ── Filters ───────────────────────────────────────────────────────

  applyFilters() {
    if (this.hasFiltersTarget) this.filtersTarget.requestSubmit()
  }

  // ── New task: a centred modal, like every other "add" in the app ──

  openNew() {
    if (!this.hasNewModalTarget) return
    this.newModalTarget.classList.remove("hidden")
    this.newModalTarget.classList.add("flex")
    requestAnimationFrame(() => this.newFormTarget.querySelector("[data-field='title']")?.focus())
  }

  closeNew() {
    if (!this.hasNewModalTarget || this.newModalTarget.classList.contains("hidden")) return
    this.newModalTarget.classList.add("hidden")
    this.newModalTarget.classList.remove("flex")
  }

  _resetNew() {
    const form = this.newFormTarget
    form.querySelectorAll("[data-field]").forEach((el) => {
      if (el.tagName === "SELECT") el.selectedIndex = Math.max(0, [...el.options].findIndex((o) => o.defaultSelected))
      else el.value = ""
    })
    if (this.hasNewDueTarget) {
      const fp = this.newDueTarget._flatpickr
      if (fp) fp.clear()
      this.newDueTarget.value = ""
    }
    this.newPeopleTarget.querySelectorAll("[data-member-id]").forEach((el) => el.remove())
    this._newFiles = []
    if (this.hasNewFileChipsTarget) this.newFileChipsTarget.innerHTML = ""
    const picker = this.newPeopleTarget.querySelector("select")
    if (picker) { picker.value = ""; [...picker.options].forEach((o) => { o.hidden = false }) }
  }

  addNewPerson(event) {
    const select = event.target
    const id = select.value
    if (!id) return
    const name = select.options[select.selectedIndex]?.text || id
    if (!this.newPeopleTarget.querySelector(`[data-member-id="${id}"]`)) {
      const chip = document.createElement("span")
      chip.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[13px]"
      chip.dataset.memberId = id
      chip.innerHTML = `${this._esc(name)} <button type="button" data-action="click->team-tasks#removeNewPerson" class="text-gray-400 hover:text-red-500 cursor-pointer">×</button>`
      select.before(chip)
      select.options[select.selectedIndex].hidden = true
    }
    select.value = ""
  }

  removeNewPerson(event) {
    const chip = event.currentTarget.closest("[data-member-id]")
    if (!chip) return
    const picker = this.newPeopleTarget.querySelector("select")
    const opt = picker && [...picker.options].find((o) => o.value === chip.dataset.memberId)
    if (opt) opt.hidden = false
    chip.remove()
  }

  _newMemberIds() {
    return Array.from(this.newPeopleTarget.querySelectorAll("[data-member-id]")).map((el) => el.dataset.memberId)
  }

  // ── Existing task: the same kind of centred modal ─────────────────

  open(event) {
    if (this._justDragged) return
    this.openById(event.currentTarget.dataset.taskId)
  }

  async openById(id) {
    const data = await this._get(`${this.tasksUrlValue}/${id}`)
    if (data) this._showPanel(data.detail)
  }

  _showPanel(html) {
    this.panelBodyTarget.innerHTML = html
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
  }

  close() {
    if (this.modalTarget.classList.contains("hidden")) return
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
    this.panelBodyTarget.innerHTML = ""
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _detail() {
    return this.hasDetailTarget ? this.detailTarget : null
  }

  _taskId() {
    return this._detail()?.dataset.taskId
  }

  // ── Create ────────────────────────────────────────────────────────

  async create() {
    if (!this.hasNewFormTarget) return
    const task = {}
    this.newFormTarget.querySelectorAll("[data-field]").forEach((el) => { task[el.dataset.field] = el.value })
    task.member_ids = this._newMemberIds()
    task.attachment_ids = this._newFiles.filter((f) => f.id).map((f) => f.id)
    if (this._newFiles.some((f) => f.uploading)) {
      this._toast(this._t("still_uploading", "Wait for the files to finish uploading."), "error")
      return
    }
    if (!task.title || !task.title.trim()) {
      this._toast(this._t("needs_title", "Give the task a title first."), "error")
      this.newFormTarget.querySelector("[data-field='title']")?.focus()
      return
    }

    if (this.hasCreateBtnTarget) this.createBtnTarget.disabled = true
    const data = await this._send("POST", this.tasksUrlValue, { task })
    if (this.hasCreateBtnTarget) this.createBtnTarget.disabled = false
    if (!data) return

    this._insertRow(data)
    this._resetNew()
    this.closeNew()
    this._toast(this._t("created", "Task created"))
  }

  // ── Field autosave ────────────────────────────────────────────────

  async saveField(event) {
    const el = event.target
    const detail = this._detail()
    if (!detail || detail.dataset.persisted !== "true") return
    const field = el.dataset.field
    if (!field) return
    const value = el.value
    const key = `${field}=${value}`
    if (el.dataset.lastSaved === key) return

    const data = await this._send("PATCH", `${this.tasksUrlValue}/${detail.dataset.taskId}`, { task: { [field]: value } })
    if (!data) return
    el.dataset.lastSaved = key
    this._replaceRow(data)
    if (data.people && this.hasPeopleTarget) this.peopleTarget.innerHTML = data.people
    if (field === "status") this._toast(data.status === "done" ? this._t("marked_done", "Marked done") : this._t("saved", "Saved"))
    this._recount()
  }

  // ── People ────────────────────────────────────────────────────────

  _memberIds() {
    if (!this.hasPeopleTarget) return []
    return Array.from(this.peopleTarget.querySelectorAll("[data-member-id]")).map((el) => el.dataset.memberId)
  }

  async addPerson(event) {
    const id = event.target.value
    if (!id) return
    await this._saveMembers([...this._memberIds(), id])
  }

  async removePerson(event) {
    const id = event.currentTarget.dataset.userId
    await this._saveMembers(this._memberIds().filter((x) => x !== id))
  }

  async _saveMembers(ids) {
    const taskId = this._taskId()
    if (!taskId) return
    const data = await this._send("PATCH", `${this.tasksUrlValue}/${taskId}`, { task: { member_ids: ids } })
    if (data?.people && this.hasPeopleTarget) this.peopleTarget.innerHTML = data.people
  }

  // ── Files: presign → S3 PUT → register → attach ───────────────────
  // Same chips as everywhere else: blue with a spinner while uploading,
  // gray with a document icon when done. Uploads run in parallel.

  pickNewFiles() { this.newFileInputTarget.click() }
  newFilesPicked(event) { this._uploadFiles([...event.target.files], this.newFileChipsTarget, this._newFiles); event.target.value = "" }
  dragOverNew(event) { event.preventDefault(); this.newDropzoneTarget.classList.add("border-blue-400", "bg-blue-50") }
  dragLeaveNew() { this.newDropzoneTarget.classList.remove("border-blue-400", "bg-blue-50") }
  dropNewFiles(event) { event.preventDefault(); this.dragLeaveNew(); this._uploadFiles([...event.dataTransfer.files], this.newFileChipsTarget, this._newFiles) }

  pickFiles() { this.fileInputTarget.click() }
  filesPicked(event) { this._uploadFiles([...event.target.files], this.fileChipsTarget, [], (id) => this._attachFile(id)); event.target.value = "" }
  dragOver(event) { event.preventDefault(); this.dropzoneTarget.classList.add("border-blue-400", "bg-blue-50") }
  dragLeave() { this.dropzoneTarget.classList.remove("border-blue-400", "bg-blue-50") }
  dropFiles(event) { event.preventDefault(); this.dragLeave(); this._uploadFiles([...event.dataTransfer.files], this.fileChipsTarget, [], (id) => this._attachFile(id)) }

  // An existing task adopts each file the moment it is registered.
  async _attachFile(id) {
    const taskId = this._taskId()
    if (!taskId) return
    const data = await this._send("PATCH", `${this.tasksUrlValue}/${taskId}`, { task: { attachment_ids: [id] } })
    if (!data) return
    if (data.files && this.hasFilesTarget) this.filesTarget.innerHTML = data.files
    this._replaceRow(data)
    this._toast(this._t("file_attached", "File attached"))
  }

  async removeFile(event) {
    const fileId = event.currentTarget.dataset.fileId
    const taskId = this._taskId()
    if (!taskId || !fileId) return
    const data = await this._send("DELETE", `${this.tasksUrlValue}/${taskId}/attachments/${fileId}`)
    if (!data) return
    if (data.files && this.hasFilesTarget) this.filesTarget.innerHTML = data.files
    this._replaceRow(data)
    this._toast(this._t("file_removed", "File removed"))
  }

  async _uploadFiles(files, chipsEl, list, onRegistered = null) {
    if (!files.length || !chipsEl) return
    const entries = files.map((file) => {
      const chip = document.createElement("span")
      chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-50 text-blue-700 text-[13px]"
      chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg><span>${this._esc(file.name)}</span>`
      chipsEl.appendChild(chip)
      const entry = { file, chip, uploading: true, id: null }
      list.push(entry)
      return entry
    })

    let presigned
    try {
      const res = await fetch(`${this.tasksUrlValue}/presign_upload`, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: entries.map((e) => ({ filename: e.file.name, content_type: e.file.type || "application/octet-stream", byte_size: e.file.size })) })
      })
      presigned = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(presigned.error || this._t("upload_failed", "Upload failed."))
    } catch (err) {
      entries.forEach((e) => { e.chip.remove(); const i = list.indexOf(e); if (i >= 0) list.splice(i, 1) })
      this._toast(err.message || this._t("upload_failed", "Upload failed."), "error")
      return
    }

    await Promise.all(presigned.uploads.map(async (upload, i) => {
      const entry = entries[i]
      try {
        const put = await fetch(upload.upload_url, { method: "PUT", headers: { "Content-Type": entry.file.type || upload.content_type }, body: entry.file })
        if (!put.ok) throw new Error(`${this._t("upload_failed", "Upload failed.")} (${put.status})`)
        const reg = await fetch(`${this.tasksUrlValue}/register_upload`, {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ files: [{ cache_name: upload.cache_name, filename: upload.filename }] })
        })
        const registered = await reg.json().catch(() => [])
        if (!reg.ok || !registered[0]) throw new Error(this._t("upload_failed", "Upload failed."))
        entry.id = registered[0].id
        entry.uploading = false
        this._finishChip(entry, list)
        if (onRegistered) { entry.chip.remove(); const j = list.indexOf(entry); if (j >= 0) list.splice(j, 1); await onRegistered(entry.id) }
      } catch (err) {
        entry.chip.remove()
        const j = list.indexOf(entry); if (j >= 0) list.splice(j, 1)
        this._toast(`${entry.file.name}: ${err.message}`, "error")
      }
    }))
  }

  _finishChip(entry, list) {
    const chip = entry.chip
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-[13px]"
    chip.innerHTML = `<svg class="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg><span>${this._esc(entry.file.name)}</span>`
    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "text-gray-400 hover:text-red-500 cursor-pointer"
    remove.innerHTML = "&times;"
    remove.addEventListener("click", (e) => { e.stopPropagation(); chip.remove(); const i = list.indexOf(entry); if (i >= 0) list.splice(i, 1) })
    chip.appendChild(remove)
  }

  // ── Comments ──────────────────────────────────────────────────────

  async comment(event) {
    if (event?.type === "keydown") event.preventDefault()
    const taskId = this._taskId()
    if (!taskId || !this.hasCommentInputTarget) return
    const body = this.commentInputTarget.value.trim()
    if (!body) return

    const data = await this._send("POST", `${this.tasksUrlValue}/${taskId}/comments`, { comment: { body } })
    if (!data) return
    this.commentsTarget.insertAdjacentHTML("beforeend", data.html)
    this.commentInputTarget.value = ""
    if (this.hasCommentCountTarget) this.commentCountTarget.textContent = `(${data.count})`
    this._replaceRow(data)
    this._toast(this._t("comment_added", "Comment added"))
  }

  // ── Delete ────────────────────────────────────────────────────────

  confirmDelete() {
    this.deleteModalTarget.classList.remove("hidden")
    this.deleteModalTarget.classList.add("flex")
  }

  hideDeleteModal() {
    this.deleteModalTarget.classList.add("hidden")
    this.deleteModalTarget.classList.remove("flex")
  }

  async destroy() {
    const taskId = this._taskId()
    this.hideDeleteModal()
    if (!taskId) return
    const data = await this._send("DELETE", `${this.tasksUrlValue}/${taskId}`)
    if (!data) return
    const row = this._row(data.id)
    if (row) {
      const tbody = row.parentElement
      row.remove()
      this._afterRemove(tbody)
    }
    this.close()
    this._updateEmpty()
    this._recount()
    this._toast(this._t("deleted", "Task deleted"))
  }

  // ── Rows ──────────────────────────────────────────────────────────

  _row(id) {
    return this.element.querySelector(`tr[data-task-id="${id}"]`)
  }

  _replaceRow(data) {
    if (!data?.row) return
    const row = this._row(data.id)
    if (row) row.outerHTML = data.row
    this._armDrag()
  }

  _insertRow(data) {
    if (!data?.row) return
    if (this.viewValue === "by_project") {
      const key = data.project_id || "none"
      let group = this.element.querySelector(`tbody[data-group="${key}"]`)
      if (!group) group = this._createGroup(key, data.project_name)
      group.insertAdjacentHTML("beforeend", data.row)
      const count = group.querySelector("[data-group-count]")
      if (count) count.textContent = group.querySelectorAll("tr[data-task-id]").length
    } else if (this.hasListTarget) {
      this.listTarget.insertAdjacentHTML(this._sortable ? "beforeend" : "afterbegin", data.row)
    }
    this._updateEmpty()
    this._recount()
    this._armDrag()
  }

  _createGroup(key, name) {
    const tbody = document.createElement("tbody")
    tbody.dataset.group = key
    tbody.setAttribute("data-team-tasks-target", "group")
    tbody.className = "divide-y divide-gray-200 border-t border-gray-200"
    const label = key === "none" ? (this.element.dataset.noProjectLabel || "No project") : (name || "")
    tbody.setAttribute("data-action", "dragover->team-tasks#dragOverGroup dragleave->team-tasks#dragLeaveGroup drop->team-tasks#dropOnGroup")
    tbody.innerHTML = `<tr class="bg-gray-50"><td colspan="7" class="px-3 py-1.5 font-semibold text-gray-600">${this._esc(label)} <span class="text-gray-400 font-normal ml-1" data-group-count>0</span></td></tr>`
    const table = this.tableTarget
    const none = table.querySelector('tbody[data-group="none"]')
    if (key !== "none" && none) table.insertBefore(tbody, none)
    else table.appendChild(tbody)
    return tbody
  }

  _afterRemove(tbody) {
    if (tbody?.dataset?.group && !tbody.querySelector("tr[data-task-id]")) tbody.remove()
  }

  _updateEmpty() {
    const any = !!this.element.querySelector("tr[data-task-id]")
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", any)
    if (this.hasTableTarget) this.tableTarget.classList.toggle("hidden", !any)
  }

  _recount() {
    if (!this.hasOpenCountTarget) return
    const open = this.element.querySelectorAll("tr[data-task-id]:not(.opacity-60)").length
    this.openCountTarget.textContent = open === 1 ? this._t("one_open", "1 open") : this._t("n_open", "%{count} open").replace("%{count}", open)
  }

  // ── Wire ──────────────────────────────────────────────────────────

  async _get(url) {
    try {
      const resp = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) { this._toast(data.error || this._t("error", "Something went wrong."), "error"); return null }
      return data
    } catch {
      this._toast(this._t("network", "Network error. Please try again."), "error")
      return null
    }
  }

  async _send(method, url, body) {
    try {
      const resp = await fetch(url, { method, headers: this._headers(), body: body ? JSON.stringify(body) : undefined })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) { this._toast(data.error || this._t("error", "Something went wrong."), "error"); return null }
      return data
    } catch {
      this._toast(this._t("network", "Network error. Please try again."), "error")
      return null
    }
  }

  _headers() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token || "" }
  }

  // Short UI strings, overridable per locale from data-i18n-* on the root.
  _t(key, fallback) {
    return this.element.dataset[`i18n${key.charAt(0).toUpperCase()}${key.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`] || fallback
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }

  _toast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
